<template>
  <!-- Mode avec résultats de recherche : toujours groupé par date -->
  <template v-if="!showDatePerTrip && trips.length">
    <div v-for="group in groupedTrips" :key="group.date" class="date-group">
      <div class="page-header">
        <h2>{{ formatGroupDate(group.date) }}</h2>
        <p class="results-count">
          {{ group.trips.length }} {{ getTripsLabel(group.trips.length) }}
        </p>
      </div>

      <TransitionGroup name="fade" tag="div" class="trip-list-items">
        <TripCard v-for="trip in group.trips" :key="trip.uuid" :trip="trip" :show-date="false" />
      </TransitionGroup>
    </div>
  </template>

  <!-- Mode groupé par date (page d'accueil) -->
  <template v-else-if="showDatePerTrip">
    <!-- ... inchangé ... -->
  </template>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import TripCard from './TripCard.vue'

const isMobile = ref(window.innerWidth <= 768)

function updateIsMobile() {
  isMobile.value = window.innerWidth <= 768
}

const props = defineProps({
  trips: {
    type: Array,
    required: true
  },
  date: {
    type: String,
    default: ''
  },
  showDatePerTrip: {
    type: Boolean,
    default: false
  },
  itemsPerPage: {
    type: Number,
    default: 5
  }
})

const visibleCount = ref(props.itemsPerPage)
let isLoadingMore = false

function extractDateKey(dateTimeString) {
  if (!dateTimeString) return 'unknown'

  return dateTimeString.includes('T')
    ? dateTimeString.split('T')[0]
    : dateTimeString.split(' ')[0]
}

function parseLocalDate(dateString) {
  if (!dateString || dateString === 'unknown') return null

  const [year, month, day] = dateString.split('-').map(Number)
  return new Date(year, month - 1, day)
}

// Grouper les trajets par date de départ
const groupedTrips = computed(() => {
  const groups = {}

  props.trips.forEach(trip => {
    const dateKey = extractDateKey(trip.timeToStart)

    if (!groups[dateKey]) {
      groups[dateKey] = []
    }

    groups[dateKey].push(trip)
  })

  return Object.keys(groups)
    .sort((a, b) => parseLocalDate(b) - parseLocalDate(a))
    .map(date => ({
      date,
      trips: groups[date]
    }))
})

const tripDates = computed(() => groupedTrips.value.map(group => group.date))

const resultsTitle = computed(() => {
  if (!tripDates.value.length) return ''

  if (tripDates.value.length === 1) {
    return formatGroupDate(tripDates.value[0])
  }

  const first = formatGroupDate(tripDates.value[tripDates.value.length - 1])
  const last = formatGroupDate(tripDates.value[0])

  return `${first} → ${last}`
})

const visibleTrips = computed(() =>
  props.trips.slice(0, visibleCount.value)
)

const hasMore = computed(() => {
  if (props.showDatePerTrip || !props.trips.length) {
    return false
  }

  return visibleCount.value < props.trips.length
})

const tripsLabel = computed(() =>
  props.trips.length > 1 ? 'trajets trouvés' : 'trajet trouvé'
)

function getTripsLabel(count) {
  return count > 1 ? 'trajets trouvés' : 'trajet trouvé'
}

function formatGroupDate(dateString) {
  const date = parseLocalDate(dateString)
  if (!date) return ''

  if (isMobile.value) {
    const day = date.getDate()
    const monthNames = [
      'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ]
    const month = monthNames[date.getMonth()]

    return `${day} ${month}`
  }

  const options = {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  }

  const formatted = date.toLocaleDateString('fr-FR', options)
  return formatted.charAt(0).toUpperCase() + formatted.slice(1)
}

function loadMore() {
  if (isLoadingMore || !hasMore.value) return

  isLoadingMore = true
  visibleCount.value += props.itemsPerPage

  nextTick(() => {
    isLoadingMore = false
  })
}

function handleScroll() {
  const scrollHeight = document.documentElement.scrollHeight
  const scrollTop = window.scrollY || document.documentElement.scrollTop
  const clientHeight = window.innerHeight

  const isMobileViewport = window.innerWidth <= 600
  const threshold = isMobileViewport ? 900 : 200

  const nearBottom = scrollHeight - scrollTop - clientHeight <= threshold

  if (nearBottom && hasMore.value) {
    loadMore()
  }
}

watch(() => props.trips, () => {
  visibleCount.value = props.itemsPerPage
  isLoadingMore = false

  nextTick(() => {
    handleScroll()
  })
})

onMounted(() => {
  window.addEventListener('scroll', handleScroll)
  window.addEventListener('resize', updateIsMobile)
  nextTick(handleScroll)
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
  window.removeEventListener('resize', updateIsMobile)
})
</script>

<style scoped>
.trip-list-results .page-header {
  padding-top: 3rem;
}

.page-header {
  padding: 2rem 0;
}

.page-header h2 {
  line-height: 1.2;
}

.results-count {
  margin-top: 0;
  font-size: 1rem;
}

.trip-list-items {
  display: flex;
  flex-direction: column;
  margin: auto;
  max-width: 900px;
  padding: 2rem 0 2rem;
  gap: 2rem;
  width: 100%;
  padding: 1rem;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-move {
  transition: transform 0.3s ease;
}

@media (max-width: 768px) {
  .page-header {
    padding-bottom: 2rem;
  }

  .trip-list-items {
    padding: 0;
  }
}
</style>
